import React, { useState, useEffect, useRef } from 'react';
import {
  FileText,
  Users,
  MapPin,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp
} from 'lucide-react';
import axios from 'axios';
import { api, formatDate, getStatusColor, getStatusIcon } from '../services/api';
import toast from 'react-hot-toast';
import TrackingMap from '../components/TrackingMap';

interface DashboardStats {
  totalOS: number;
  osPendentes: number;
  osEmAndamento: number;
  osConcluidas: number;
  totalUsuarios: number;
  motoristasAtivos: number;
}

interface RecentOS {
  id: number;
  codigo_acesso: string;
  nome_falecido: string;
  status_geral: string;
  criado_em: string;
  nome_motorista?: string;
}

const Dashboard: React.FC = () => {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [recentOS, setRecentOS] = useState<RecentOS[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const requestIdRef = useRef(0);
  const hasLoadedRef = useRef(false);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    loadDashboardData();
    const intervalId = setInterval(() => loadDashboardData({ silent: true }), 2 * 60 * 1000);
    return () => {
      clearInterval(intervalId);
      abortRef.current?.abort();
    };
  }, []);

  const loadDashboardData = async (options?: { silent?: boolean }) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    const requestId = ++requestIdRef.current;

    try {
      if (!hasLoadedRef.current && !options?.silent) {
        setIsLoading(true);
      }

      const requestConfig = { signal: controller.signal, timeout: 60000 };

      const [statsResponse, usersResponse] = await Promise.all([
        api.get('/os', requestConfig),
        api.get('/usuarios?funcao=motorista&ativo=true', requestConfig)
      ]);

      if (requestId !== requestIdRef.current) return;

      const osData = statsResponse.data;
      const motoristas = usersResponse.data.users || usersResponse.data.usuarios || [];

      const nextStats: DashboardStats = {
        totalOS: osData.pagination?.total || osData.os?.length || 0,
        osPendentes: osData.os?.filter((os: any) => os.status_geral === 'Pendente').length || 0,
        osEmAndamento: osData.os?.filter((os: any) => os.status_geral === 'Em Andamento').length || 0,
        osConcluidas: osData.os?.filter((os: any) => os.status_geral === 'Concluída').length || 0,
        totalUsuarios: 0,
        motoristasAtivos: motoristas.length
      };

      setStats(nextStats);
      setRecentOS(osData.os?.slice(0, 5) || []);
      hasLoadedRef.current = true;
    } catch (error: any) {
      if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') return;
      if (requestId !== requestIdRef.current) return;

      console.error('Erro no dashboard:', error);

      if (error.response?.status === 429) {
        toast.error('Muitas requisições. Aguarde um momento...');
      } else if (!hasLoadedRef.current) {
        toast.error('Erro ao carregar dados do dashboard');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const StatCard: React.FC<{
    title: string;
    value: string | number;
    icon: React.ReactNode;
    color: string;
    change?: string;
  }> = ({ title, value, icon, color, change }) => (
    <div className="bg-white overflow-hidden shadow rounded-lg">
      <div className="p-5">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`w-8 h-8 rounded-md flex items-center justify-center ${color}`}>
              {icon}
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 truncate">{title}</dt>
              <dd className="text-lg font-medium text-gray-900">{value}</dd>
            </dl>
          </div>
        </div>
        {change && (
          <div className="mt-2">
            <span className="text-sm text-green-600 flex items-center">
              <TrendingUp className="h-4 w-4 mr-1" />
              {change}
            </span>
          </div>
        )}
      </div>
    </div>
  );

  if (isLoading && !hasLoadedRef.current) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="mt-2 text-gray-600">Visão geral do sistema de Ordens de Serviço</p>
        </div>
        <div className="flex items-center justify-center h-40 mb-8">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
            <p className="text-sm text-gray-600">Carregando estatísticas...</p>
          </div>
        </div>
        <TrackingMap className="h-96" />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Título */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
        <p className="mt-2 text-gray-600">
          Visão geral do sistema de Ordens de Serviço
        </p>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <StatCard
          title="Total de OSs"
          value={stats?.totalOS || 0}
          icon={<FileText className="h-5 w-5 text-white" />}
          color="bg-blue-500"
        />
        <StatCard
          title="OSs Pendentes"
          value={stats?.osPendentes || 0}
          icon={<Clock className="h-5 w-5 text-white" />}
          color="bg-yellow-500"
        />
        <StatCard
          title="OSs em Andamento"
          value={stats?.osEmAndamento || 0}
          icon={<AlertCircle className="h-5 w-5 text-white" />}
          color="bg-orange-500"
        />
        <StatCard
          title="OSs Concluídas"
          value={stats?.osConcluidas || 0}
          icon={<CheckCircle className="h-5 w-5 text-white" />}
          color="bg-green-500"
        />
      </div>

      {/* Mapa de Rastreamento */}
      <div className="mb-8">
        <TrackingMap className="h-96" />
      </div>

      {/* OSs Recentes */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            OSs Recentes
          </h3>
          <div className="overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    OS
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Falecido
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Motorista
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Criado em
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentOS.map((os) => (
                  <tr key={os.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {os.codigo_acesso}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {os.nome_falecido}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(os.status_geral)}`}>
                        {getStatusIcon(os.status_geral)} {os.status_geral}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {os.nome_motorista || '-'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(os.criado_em)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Ações Rápidas */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
            Ações Rápidas
          </h3>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <a
              href="/ordens-servico"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-blue-50 text-blue-700 ring-4 ring-white">
                  <FileText className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Nova OS
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Criar uma nova Ordem de Serviço
                </p>
              </div>
              <span
                className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </a>

            <a
              href="/usuarios"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-green-50 text-green-700 ring-4 ring-white">
                  <Users className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Novo Usuário
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Cadastrar motorista ou administrador
                </p>
              </div>
              <span
                className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </a>

            <a
              href="/ordens-servico"
              className="relative group bg-white p-6 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500 rounded-lg border border-gray-200 hover:border-blue-300 transition-colors"
            >
              <div>
                <span className="rounded-lg inline-flex p-3 bg-purple-50 text-purple-700 ring-4 ring-white">
                  <MapPin className="h-6 w-6" />
                </span>
              </div>
              <div className="mt-8">
                <h3 className="text-lg font-medium">
                  <span className="absolute inset-0" aria-hidden="true" />
                  Gerenciar OSs
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  Visualizar e gerenciar todas as OSs
                </p>
              </div>
              <span
                className="pointer-events-none absolute top-6 right-6 text-gray-300 group-hover:text-gray-400"
                aria-hidden="true"
              >
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20 4h1a1 1 0 00-1-1v1zm-1 12a1 1 0 102 0h-2zM8 3a1 1 0 000 2V3zM3.293 19.293a1 1 0 101.414 1.414l-1.414-1.414zM19 4v12h2V4h-2zm1-1H8v2h12V3zm-.707.293l-16 16 1.414 1.414 16-16-1.414-1.414z" />
                </svg>
              </span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;



